import { type ReactNode, createContext, useContext, useMemo } from 'react';
import type { VariableDefinition } from '@repo/videa-components/src/ui/variable-textarea/types';

import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';

/**
 * Note result context value interface.
 * Contains state and handlers for note viewing/editing functionality.
 */
export interface VideaNotesNoteResultContextValue {
  // Note data
  clinicalNotesData: ClinicalNote[];
  selectedNote: ClinicalNote | undefined;
  noteContent: string;
  patientConsentBlurb: string | undefined;

  // Note status flags
  isNoteReadOnly: boolean;
  isNoteSavedToPMS: boolean;
  isNoteBeingSavedToPMS: boolean;
  isSavingNoteUpdate: boolean;
  isSavingNoteToPMS: boolean;
  isRetryingWriteback: boolean;
  hasWritebackEnabled: boolean;
  hasBeenEdited: boolean;

  // UI state
  showCopyTooltip: boolean;

  // Template/field data
  variableDefinitions: VariableDefinition[];

  // Note handlers
  onNoteChange: (value: string) => void;
  onNoteClick: () => void;
  onSelectedNote: (noteId: string | undefined) => void;
  onCopyText: (text: string) => void;
  onCreateAdditionalNote: () => void;
  onSaveNoteToPMS: () => void;
  onRetryWritebackToPMS: () => void;
  onResetNote: () => void;
  onVariableValueChange: (varName: string, newValue: string) => void;
}

const VideaNotesNoteResultContext =
  createContext<VideaNotesNoteResultContextValue | null>(null);

interface VideaNotesNoteResultProviderProps {
  value: VideaNotesNoteResultContextValue;
  children: ReactNode;
}

/**
 * Provider component for VideaNotes note result context.
 * Contains state and handlers for note viewing/editing functionality.
 */
export const VideaNotesNoteResultProvider = ({
  value,
  children,
}: VideaNotesNoteResultProviderProps) => {
  return (
    <VideaNotesNoteResultContext.Provider value={value}>
      {children}
    </VideaNotesNoteResultContext.Provider>
  );
};

/**
 * Hook to access VideaNotes note result context.
 * Must be used within a VideaNotesNoteResultProvider.
 */
export const useVideaNotesNoteResultContext =
  (): VideaNotesNoteResultContextValue => {
    const context = useContext(VideaNotesNoteResultContext);
    if (!context) {
      throw new Error(
        'useVideaNotesNoteResultContext must be used within a VideaNotesNoteResultProvider',
      );
    }
    return context;
  };

/**
 * Hook to access note data from note result context.
 * Memoized to prevent unnecessary re-renders.
 */
export const useNoteData = () => {
  const {
    clinicalNotesData,
    selectedNote,
    noteContent,
    patientConsentBlurb,
    variableDefinitions,
  } = useVideaNotesNoteResultContext();
  return useMemo(
    () => ({
      clinicalNotesData,
      selectedNote,
      noteContent,
      patientConsentBlurb,
      variableDefinitions,
    }),
    [
      clinicalNotesData,
      selectedNote,
      noteContent,
      patientConsentBlurb,
      variableDefinitions,
    ],
  );
};

/**
 * Hook to access note status flags from note result context.
 * Memoized to prevent unnecessary re-renders.
 */
export const useNoteStatus = () => {
  const {
    isNoteReadOnly,
    isNoteSavedToPMS,
    isNoteBeingSavedToPMS,
    isSavingNoteUpdate,
    isSavingNoteToPMS,
    isRetryingWriteback,
    hasWritebackEnabled,
    hasBeenEdited,
  } = useVideaNotesNoteResultContext();
  return useMemo(
    () => ({
      isNoteReadOnly,
      isNoteSavedToPMS,
      isNoteBeingSavedToPMS,
      isSavingNoteUpdate,
      isSavingNoteToPMS,
      isRetryingWriteback,
      hasWritebackEnabled,
      hasBeenEdited,
    }),
    [
      isNoteReadOnly,
      isNoteSavedToPMS,
      isNoteBeingSavedToPMS,
      isSavingNoteUpdate,
      isSavingNoteToPMS,
      isRetryingWriteback,
      hasWritebackEnabled,
      hasBeenEdited,
    ],
  );
};

/**
 * Hook to access note handlers from note result context.
 * Memoized to prevent unnecessary re-renders.
 */
export const useNoteHandlers = () => {
  const {
    onNoteChange,
    onNoteClick,
    onSelectedNote,
    onCopyText,
    onCreateAdditionalNote,
    onSaveNoteToPMS,
    onRetryWritebackToPMS,
    onResetNote,
    onVariableValueChange,
  } = useVideaNotesNoteResultContext();
  return useMemo(
    () => ({
      onNoteChange,
      onNoteClick,
      onSelectedNote,
      onCopyText,
      onCreateAdditionalNote,
      onSaveNoteToPMS,
      onRetryWritebackToPMS,
      onResetNote,
      onVariableValueChange,
    }),
    [
      onNoteChange,
      onNoteClick,
      onSelectedNote,
      onCopyText,
      onCreateAdditionalNote,
      onSaveNoteToPMS,
      onRetryWritebackToPMS,
      onResetNote,
      onVariableValueChange,
    ],
  );
};
